import React, {
  useState,
  useMemo,
  useRef,
  ReactNode,
  cloneElement,
  isValidElement,
  Children,
  ReactElement,
  useEffect,
  useCallback,
} from 'react';
import classes from './ExpandableTextAreaResult.module.scss';
import classNames from 'classnames';
import useExpandableResult from 'js/components/pipelines/cards/components/ExpandableResult/useExpandableResult';

interface ExpandableTextAreaResultProps {
  children?: ReactNode;
  value?: string;
  className?: string;
}

export const ExpandableResult = (props: ExpandableTextAreaResultProps) => {
  const { children, value, className } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { isExpanded, toggleExpand, displayHeight, isScrolling, needsExpansion, maxHeight } =
    useExpandableResult(textAreaRef, contentRef, children, value);

  // Enhanced child component with ref and style
  const enhancedChild = useMemo(() => {
    // Legacy mode - render a textarea with the value
    if (!children) {
      return (
        <textarea
          ref={textAreaRef}
          readOnly
          style={displayHeight ? { height: displayHeight } : undefined}
          className={classNames('nowheel nodrag', classes.textAreaResult, className)}
          value={value}
        />
      );
    }

    // Single React element that can be cloned
    if (isValidElement(children)) {
      const child = Children.only(children) as ReactElement;

      return cloneElement(child, {
        ref: contentRef,
        style: {
          ...child.props.style,
          height: displayHeight,
          maxHeight: isExpanded ? 'none' : maxHeight,
          overflow: 'auto',
        },
        className: classNames(child.props.className, 'nowheel nodrag', classes.content, className),
      });
    }

    // Multiple children or text nodes - wrap in a div
    return (
      <div
        ref={contentRef}
        style={{ height: displayHeight, overflow: 'hidden' }}
        className={classNames(classes.content, className, 'nowheel nodrag')}
      >
        {children}
      </div>
    );
  }, [children, value, displayHeight, isExpanded, maxHeight, className]);

  return (
    <div
      className={classNames(classes.container, {
        [classes.expanded]: isExpanded,
      })}
    >
      {enhancedChild}
      {needsExpansion && (
        <>
          {!isExpanded && (
            <div
              className={classNames(classes.gradientOverlay, {
                [classes.gradientOverlayFade]: isScrolling,
              })}
            />
          )}
          <button
            className={classNames(classes.expandButton, {
              [classes.expandButtonFade]: isScrolling,
            })}
            onClick={toggleExpand}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </>
      )}
    </div>
  );
};
